import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';

import { Column, Row } from '~components/table-view';

import { User } from '~main/reducers/login';

import ActionButtons from './ActionButtons';
import AlertBadge from './AlertBadge';
import BudgetAlertBadges from './BudgetAlertBadges';

const columnsFactory = (
  openEdit: (id: string) => void,
  openTracker: (id: string) => void,
  user?: User,
  openCopy?: (id: string) => void,
  onUpdateRevision?: (id: string) => void,
) => {
  const columns: Column[] = [
    {
      id: 'reference',
      header: 'Reference',
      type: 'text',
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
    },
    {
      id: 'full_name',
      header: 'Client',
      type: 'text',
      deriveValue: (row) => row.client?.full_name,
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
    },
    {
      id: 'case_manager',
      header: 'Case Manager',
      type: 'text',
      deriveValue: (row) => row.client?.case_manager_user?.full_name ?? '-',
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
    },
    {
      id: 'price_plan_revision',
      header: 'Price Plan',
      type: 'custom',
      component: AlertBadge,
      componentProps: (row: Row) => {
        const { id, price_plan_revision } = row ?? {};
        const { price_plan, sequence, is_latest_revision } = price_plan_revision ?? {};

        let tooltip: string | undefined;
        let showAlert = false;
        let color: string | undefined;
        if (price_plan?.is_deleted) {
          showAlert = true;
          color = 'red';
          tooltip = 'Price plan is deleted';
        } else if (!is_latest_revision) {
          showAlert = true;
          color = 'orange';
          tooltip = 'Price plan revision is outdated';
        }

        return {
          id,
          label: price_plan?.name && sequence != null ? `${price_plan?.name} v${sequence}` : '',
          showAlert,
          color,
          tooltip,
        };
      },
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
    },
    {
      id: 'funding_type',
      header: 'Funding Type',
      type: 'text',
      deriveValue: (row) => row.package_fixed?.funding_type?.name,
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
    },
    {
      id: 'status',
      header: 'Status',
      type: 'text',
      deriveValue: (row) => row.status?.name,
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
    },
    {
      id: 'start_date',
      header: 'Start Date',
      type: 'date',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'end_date',
      header: 'End Date',
      type: 'date',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'last_modified',
      header: 'Last Modified',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
      deriveValue: (row) => {
        const timestamp = row.updated_at ?? row.created_at;
        return timestamp ? dayjs(timestamp).format(dateTimeDisplayFormat) : '';
      },
    },
    {
      id: 'total_allocated',
      header: 'Allocated Budget',
      type: 'currency',
      componentProps: {
        noDecimals: true,
      },
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'available_amount',
      header: 'Available Budget',
      deriveValue: (row) => row.available_amount ?? 0,
      type: 'currency',
      componentProps: {
        noDecimals: true,
      },
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'budget_alerts',
      header: '',
      type: 'custom',
      component: BudgetAlertBadges,
      componentProps: (row: Row) => ({
        id: row.id,
        isOverAllocated: !!row.is_over_allocated,
        supplementRatesStale: !!row.supplement_rates_stale,
      }),
      classNames: {
        header: 'cell-align-left over-allocated-col',
        column: 'cell-align-left over-allocated-col',
      },
    },
    {
      id: 'actions',
      header: '',
      type: 'custom',
      component: ActionButtons,
      componentProps: (row: Row) => ({
        id: row.id,
        reference: row.reference,
        disabled: row?.price_plan_revision?.price_plan?.is_deleted === true,
        isOutdated: row.price_plan_revision?.is_latest_revision === false,
        openEdit,
        openTracker,
        openCopy,
        onUpdateRevision,
        user,
        editHref: `/support-at-home/budgets/${row.id}`,
        trackerHref: `/support-at-home/budgets/${row.id}/tracker`,
        copyHref: openCopy ? `/support-at-home/budgets/${row.id}/copy` : undefined,
      }),
    },
  ];

  return columns;
};

export default columnsFactory;
